import { logger } from '@vagabondinternet/logging';
import { DISPLAY_NAME_MESSAGES } from '@words-happen/display-name';
import { DISPLAY_NAME_SAVE_FAILED_MESSAGE } from '@words-happen/screens';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type DisplayNameSaveFailure, displayNameSaveFailure, type Profile, type ProfileApi } from '../api/profile';
import { useSession } from '../session/session-context';

/**
 * - `guest`: signed out; there is no profile.
 * - `loading`: signed in, waiting for the API.
 * - `ready`: loaded.
 * - `load-error`: loading failed, until `retryLoad` succeeds.
 */
export type ProfileStatus = 'guest' | 'loading' | 'ready' | 'load-error';

export interface ProfileState {
  status: ProfileStatus;
  /** The profile once loaded, and after each saved name. */
  profile?: Profile;
  retryLoad: () => void;
  /**
   * Saves the display name. Resolves to undefined once saved, or to the message the player sees. When the session has
   * expired, the player is sent to sign in.
   */
  saveDisplayName: (displayName: string) => Promise<string | undefined>;
}

const FAILURE_MESSAGES: Record<Exclude<DisplayNameSaveFailure['kind'], 'invalid'>, string> = {
  unavailable: DISPLAY_NAME_MESSAGES.unavailable,
  'too-many-changes': DISPLAY_NAME_MESSAGES.tooManyChanges,
  unauthenticated: DISPLAY_NAME_SAVE_FAILED_MESSAGE,
  failed: DISPLAY_NAME_SAVE_FAILED_MESSAGE,
};

const failureMessage = (failure: DisplayNameSaveFailure): string =>
  failure.kind === 'invalid' ? failure.message : FAILURE_MESSAGES[failure.kind];

/**
 * The signed-in player's profile, loaded from the API once `enabled` (e.g. when the Profile tab is first shown) and
 * again on `retryLoad`. Nothing here logs the name or the email.
 */
export const useProfile = (api: ProfileApi, { enabled = true }: { enabled?: boolean } = {}): ProfileState => {
  const { signedIn, goToSignIn } = useSession();
  const [profile, setProfile] = useState<Profile>();
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'load-error'>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new loadAttempt is how retryLoad loads again
  useEffect(() => {
    if (!signedIn) {
      setProfile(undefined);
      return;
    }
    if (!enabled) {
      return;
    }

    let current = true;
    setLoadStatus('loading');
    api.load().then(
      (loaded) => {
        if (current) {
          setProfile(loaded);
          setLoadStatus('ready');
        }
      },
      (error: unknown) => {
        logger.warn({ message: 'Loading the profile failed', error: (error as Error).message });
        if (current) {
          setLoadStatus('load-error');
        }
      },
    );
    return () => {
      current = false;
    };
  }, [signedIn, enabled, loadAttempt, api]);

  const saveDisplayName = useCallback(
    async (displayName: string) => {
      try {
        const saved = await api.saveDisplayName(displayName);
        if (mounted.current) {
          setProfile(saved);
        }
        return undefined;
      } catch (error) {
        const failure = displayNameSaveFailure(error);
        if (failure.kind === 'unauthenticated') {
          goToSignIn();
        } else if (failure.kind === 'failed') {
          logger.warn({ message: 'Saving the display name failed', error: (error as Error).message });
        }
        return failureMessage(failure);
      }
    },
    [api, goToSignIn],
  );

  const retryLoad = useCallback(() => setLoadAttempt((attempt) => attempt + 1), []);

  return {
    status: signedIn ? loadStatus : 'guest',
    profile: signedIn ? profile : undefined,
    retryLoad,
    saveDisplayName,
  };
};
